import React, { useCallback } from 'react'
import Select from 'react-select'
import { Button } from './Button'
import OverridableField from './OverridableField'
import { CATEGORY_OPTIONS, GENDER_OPTIONS, TYPE_OPTIONS } from '../constants'
import listTableStyles from '../styles/list-table.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import formStyles from '../styles/forms.module.scss'
import styles from './CompetitionsEditor.module.scss'
import { deriveCompetitionShortName } from '../utils/competitionShortName'

export interface CompetitionDraft {
    name: string
    shortName: string
    type: string
    gender: string
    category: string
}

export interface CompetitionExternal {
    name: string | null
    shortName: string | null
    type: string | null
    gender: string | null
    category: string | null
}

export interface CompetitionItem {
    id?: string                    // tc_competition_id for existing, undefined for new
    draft: CompetitionDraft        // current editable values
    external?: CompetitionExternal // ext values for existing (enables revert), undefined for new
    canDelete?: boolean            // whether this item can be deleted
}

export interface CompetitionsEditorProps {
    /** The list of competitions to edit */
    competitions: CompetitionItem[]
    /** Called when competitions change */
    onChange: (competitions: CompetitionItem[]) => void
    /** Called when delete is requested for an existing competition (by id) */
    onDelete?: (id: string, name: string) => void
    /** Optional hint text shown below the header */
    hint?: string
    /** Whether to show the header with count. Default: true */
    showHeader?: boolean
    /** Custom header label. Default: "Competitions" */
    headerLabel?: string
}

const syncDerivedShortName = (
    currentDraft: CompetitionDraft,
    nextDraft: CompetitionDraft,
    field: keyof CompetitionDraft
): CompetitionDraft => {
    if (field !== 'gender' && field !== 'type') {
        return nextDraft
    }

    const previousDerivedShortName = deriveCompetitionShortName(
        currentDraft.gender,
        currentDraft.type
    )
    const nextDerivedShortName = deriveCompetitionShortName(
        nextDraft.gender,
        nextDraft.type
    )

    if (!currentDraft.shortName || currentDraft.shortName === previousDerivedShortName) {
        return { ...nextDraft, shortName: nextDerivedShortName }
    }

    return nextDraft
}

export const CompetitionsEditor: React.FC<CompetitionsEditorProps> = ({
    competitions,
    onChange,
    onDelete,
    hint,
    showHeader = true,
    headerLabel = 'Competitions',
}) => {
    // Add a new competition row
    const handleAdd = useCallback(() => {
        onChange([...competitions, { draft: { name: '', shortName: '', type: '', gender: '', category: '' }, canDelete: true }])
    }, [competitions, onChange])

    // Update a competition field
    const handleFieldChange = useCallback((index: number, field: keyof CompetitionDraft, value: string) => {
        onChange(competitions.map((comp, i) => {
            if (i !== index) return comp

            const nextDraft = syncDerivedShortName(
                comp.draft,
                { ...comp.draft, [field]: value },
                field
            )

            return { ...comp, draft: nextDraft }
        }))
    }, [competitions, onChange])

    // Revert a field to external value
    const handleRevert = useCallback((index: number, field: keyof CompetitionDraft) => {
        const comp = competitions[index]
        if (!comp.external) return
        const extValue = comp.external[field] || ''
        onChange(competitions.map((c, i) => {
            if (i !== index) return c

            const nextDraft = syncDerivedShortName(
                c.draft,
                { ...c.draft, [field]: extValue },
                field
            )

            return { ...c, draft: nextDraft }
        }))
    }, [competitions, onChange])

    // Remove a new competition (no id)
    const handleRemove = useCallback((index: number) => {
        onChange(competitions.filter((_, i) => i !== index))
    }, [competitions, onChange])

    // Delete an existing competition (has id)
    const handleDelete = useCallback((index: number) => {
        const comp = competitions[index]
        if (comp.id && onDelete) {
            onDelete(comp.id, comp.draft.name || 'this competition')
        }
    }, [competitions, onDelete])

    return (
        <div className={formStyles.formField}>
            {showHeader && (
                <h3>{headerLabel} ({competitions.length})</h3>
            )}
            {hint && <p className={formStyles.hint}>{hint}</p>}

            <div>
                <div className={`${listTableStyles.tableHeader} ${styles.grid}`}>
                    <div>Name</div>
                    <div>Short Name</div>
                    <div>Gender</div>
                    <div>Type</div>
                    <div>Category</div>
                    <div></div>
                </div>
                <div className={listTableStyles.tableBody}>
                    {competitions.map((comp, index) => {
                        const isNew = !comp.id
                        const ext = comp.external

                        return (
                            <div
                                key={comp.id || `new-${index}`}
                                className={`${listTableStyles.tableRow} ${styles.grid} ${isNew ? styles.newRow : ''}`}
                            >
                                <div className={styles.cell}>
                                    <OverridableField
                                        tcValue={comp.draft.name}
                                        extValue={ext?.name || null}
                                        onRevert={() => handleRevert(index, 'name')}
                                        compact
                                    >
                                        <input
                                            type="text"
                                            value={comp.draft.name}
                                            onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                                            placeholder={isNew ? "Name (required)" : "Name"}
                                            className={ext && comp.draft.name !== (ext.name || '') ? 'modified' : ''}
                                        />
                                    </OverridableField>
                                </div>
                                <div className={styles.cell}>
                                    <OverridableField
                                        tcValue={comp.draft.shortName}
                                        extValue={ext?.shortName || null}
                                        onRevert={() => handleRevert(index, 'shortName')}
                                        compact
                                    >
                                        <input
                                            type="text"
                                            value={comp.draft.shortName}
                                            onChange={(e) => handleFieldChange(index, 'shortName', e.target.value)}
                                            placeholder="Short name"
                                            className={ext && comp.draft.shortName !== (ext.shortName || '') ? 'modified' : ''}
                                        />
                                    </OverridableField>
                                </div>
                                <div className={styles.cell}>
                                    <OverridableField
                                        tcValue={comp.draft.gender}
                                        extValue={ext?.gender || null}
                                        onRevert={() => handleRevert(index, 'gender')}
                                        compact
                                    >
                                        <Select
                                            value={GENDER_OPTIONS.find(o => o.value === comp.draft.gender) || null}
                                            onChange={(option) => handleFieldChange(index, 'gender', option?.value || '')}
                                            options={GENDER_OPTIONS}
                                            isClearable
                                            placeholder="Gender..."
                                            className={selectStyles.reactSelect}
                                            classNamePrefix="react-select"
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </OverridableField>
                                </div>
                                <div className={styles.cell}>
                                    <OverridableField
                                        tcValue={comp.draft.type}
                                        extValue={ext?.type || null}
                                        onRevert={() => handleRevert(index, 'type')}
                                        compact
                                    >
                                        <Select
                                            value={TYPE_OPTIONS.find(o => o.value === comp.draft.type) || null}
                                            onChange={(option) => handleFieldChange(index, 'type', option?.value || '')}
                                            options={TYPE_OPTIONS}
                                            isClearable
                                            placeholder="Type..."
                                            className={selectStyles.reactSelect}
                                            classNamePrefix="react-select"
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </OverridableField>
                                </div>
                                <div className={styles.cell}>
                                    <OverridableField
                                        tcValue={comp.draft.category}
                                        extValue={ext?.category || null}
                                        onRevert={() => handleRevert(index, 'category')}
                                        compact
                                    >
                                        <Select
                                            value={CATEGORY_OPTIONS.find(o => o.value === comp.draft.category) || null}
                                            onChange={(option) => handleFieldChange(index, 'category', option?.value || '')}
                                            options={CATEGORY_OPTIONS}
                                            isClearable
                                            placeholder="Category..."
                                            className={selectStyles.reactSelect}
                                            classNamePrefix="react-select"
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </OverridableField>
                                </div>
                                <div className={listTableStyles.cellActions}>
                                    {comp.canDelete && (
                                        <Button
                                            variant="secondary"
                                            size="small"
                                            onClick={() => isNew ? handleRemove(index) : handleDelete(index)}
                                            title={isNew ? undefined : "Delete competition"}
                                        >
                                            ✕
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
            <div className={formStyles.formActions}>
                <Button variant="secondary" onClick={handleAdd}>
                    + Add Competition
                </Button>
            </div>
        </div>
    )
}
